import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import TrainerLayout from '../../components/layout/TrainerLayout';
import ChatPanel from '../../components/ChatPanel';
import { useAuth } from '../../context/AuthContext';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Input } from '../../components/ui/input';
import { Loader2, MessageSquare, Search } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatPreview = (msg) => {
  if (!msg) return 'Sin mensajes aún';
  const t = msg.content || '';
  return t.length > 60 ? t.slice(0, 60) + '…' : t;
};

const formatTime = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const now = new Date();
    if (d.toDateString() === now.toDateString()) {
      return d.toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit' });
    }
    return d.toLocaleDateString('es', { day: '2-digit', month: '2-digit' });
  } catch {
    return '';
  }
};

const TrainerChatPage = () => {
  const { user } = useAuth();
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState('');

  const fetchThreads = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/chat/threads`);
      setThreads(r.data.threads || []);
    } catch (e) {
      console.error('threads error', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchThreads();
    const id = setInterval(fetchThreads, 10000);
    return () => clearInterval(id);
  }, [fetchThreads]);

  const filtered = threads.filter((t) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (t.peer_name || '').toLowerCase().includes(q) || (t.peer_email || '').toLowerCase().includes(q);
  });

  return (
    <TrainerLayout>
      <div className="space-y-4" data-testid="trainer-chat-page">
        <div className="flex items-center gap-2">
          <MessageSquare className="h-6 w-6 text-primary" />
          <h1 className="text-2xl font-bold text-foreground">Mensajes</h1>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 h-[75vh] min-h-[500px]">
          {/* Lista de threads */}
          <div className="md:col-span-1 bg-card border border-border rounded-lg flex flex-col overflow-hidden">
            <div className="p-3 border-b border-border">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Buscar cliente…"
                  className="pl-9 bg-background"
                  data-testid="chat-search-input"
                />
              </div>
            </div>
            <ScrollArea className="flex-1">
              {loading ? (
                <div className="flex items-center justify-center h-32">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : filtered.length === 0 ? (
                <div className="p-6 text-center text-sm text-muted-foreground">
                  {search ? 'Sin resultados' : 'Aún no tienes clientes'}
                </div>
              ) : (
                <ul className="divide-y divide-border">
                  {filtered.map((t) => {
                    const isActive = selected?.peer_id === t.peer_id;
                    return (
                      <li key={t.peer_id}>
                        <button
                          type="button"
                          onClick={() => setSelected(t)}
                          className={`w-full text-left p-3 hover:bg-zinc-900/40 transition-colors ${isActive ? 'bg-zinc-900/60' : ''}`}
                          data-testid={`chat-thread-${t.peer_id}`}
                        >
                          <div className="flex items-start gap-3">
                            <div className="relative shrink-0">
                              <div className="h-10 w-10 rounded-full bg-primary/20 flex items-center justify-center text-primary font-semibold">
                                {(t.peer_name || t.peer_email || '?').charAt(0).toUpperCase()}
                              </div>
                              {t.unread_count > 0 && (
                                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
                                  {t.unread_count > 9 ? '9+' : t.unread_count}
                                </span>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between gap-2">
                                <p className="font-medium text-foreground text-sm truncate">
                                  {t.peer_name || t.peer_email}
                                </p>
                                <span className="text-[10px] text-muted-foreground shrink-0">
                                  {formatTime(t.last_message?.sent_at)}
                                </span>
                              </div>
                              <p className={`text-xs truncate ${t.unread_count > 0 ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>
                                {formatPreview(t.last_message)}
                              </p>
                            </div>
                          </div>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </ScrollArea>
          </div>

          {/* Panel de chat */}
          <div className="md:col-span-2 h-full">
            {selected ? (
              <ChatPanel
                peer={{
                  id: selected.peer_id,
                  name: selected.peer_name,
                  email: selected.peer_email,
                  role: selected.peer_role,
                }}
                currentUserId={user?.id}
                onMessageSent={fetchThreads}
              />
            ) : (
              <div className="bg-card border border-border rounded-lg p-12 text-center text-muted-foreground h-full flex items-center justify-center">
                <div>
                  <MessageSquare className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>Selecciona un cliente para empezar a chatear</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </TrainerLayout>
  );
};

export default TrainerChatPage;
